import React, { useRef, useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import type { Assistant as Interface } from '../../design/components';
import { AdaIcon } from '../shared/AdaIcon';

// Pre-process content to fix malformed ```steps blocks and merge consecutive ones
const preprocessSteps = (content: string): string => {
  const allSteps: string[] = [];
  const processed = content.replace(/```steps\s*([\s\S]*?)```/g, (_, stepsContent) => {
    let steps = stepsContent.trim().split('\n').filter(Boolean);
    if (steps.length === 1 && steps[0].length > 50) {
      steps = steps[0].split(/(?<=[a-z.!?])\s*(?=[A-Z][a-z]+(?:ed|ing|d)\s)/).filter(Boolean);
    }
    allSteps.push(...steps);
    return '{{STEPS_PLACEHOLDER}}';
  });

  if (allSteps.length === 0) return content;

  // Deduplicate steps while preserving order
  const uniqueSteps = [...new Set(allSteps)];

  let first = true;
  return processed.replace(/\{\{STEPS_PLACEHOLDER\}\}/g, () => {
    if (first) {
      first = false;
      return '\n\n```steps\n' + uniqueSteps.join('\n') + '\n```\n\n';
    }
    return '';
  });
};

const CollapsibleSteps = ({ children }: { children: string }) => {
  const [expanded, setExpanded] = useState(false);
  const steps = children.trim().split('\n').filter(Boolean);
  const stepCount = steps.length;

  return (
    <div style={{ margin: '0.25rem 0' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          cursor: 'pointer',
          color: '#666',
          fontSize: '0.8rem',
        }}
      >
        <span style={{ fontSize: '0.7rem' }}>{expanded ? '▼' : '▶'}</span>
        {stepCount} {stepCount === 1 ? 'step' : 'steps'} completed
      </div>
      {expanded && (
        <div style={{ paddingLeft: '1rem', paddingTop: '0.25rem', fontSize: '0.8rem', color: '#666' }}>
          {steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.25rem' }}>
              <span>•</span>
              <span>{step}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Assistant: React.FC<Interface> = ({ state, onSend, disabled }) => {
  const [messages] = state || [[], () => {}];
  const [input, setInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if ((!input.trim() && files.length === 0) || disabled) return;
    const content = input.trim() || `Attached ${files.length} file${files.length === 1 ? '' : 's'}`;
    const sendFiles = files.length > 0 ? files : undefined;
    setInput('');
    setFiles([]);
    const unused = await onSend?.(content, sendFiles);
    if (unused?.length) setFiles(unused);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
    e.target.value = '';
  };

  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('image/') || f.type === 'application/pdf'
    );
    if (dropped.length > 0) setFiles(prev => [...prev, ...dropped]);
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
      onDrop={handleDrop}
    >
      {dragOver && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 10,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '2px dashed #1976d2',
          borderRadius: '4px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <span style={{ color: '#1976d2' }}>Drop files here</span>
        </div>
      )}
      {/* Header */}
      <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #ccc', display: 'flex', alignItems: 'center', gap: '0.5rem', minHeight: '49px', boxSizing: 'border-box' }}>
        <AdaIcon size={20} working={disabled} />
        <strong style={{ fontSize: '1.25rem' }}>Ada</strong>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflow: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', minHeight: 0 }}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                padding: '0.75rem 1rem',
                maxWidth: '80%',
                backgroundColor: msg.error ? '#ffcdd2' : msg.role === 'user' ? '#1976d2' : '#f5f5f5',
                color: msg.error ? '#c62828' : msg.role === 'user' ? 'white' : 'black',
                borderRadius: '0.5rem',
              }}
              className={msg.role === 'assistant' ? 'markdown-content' : ''}
            >
              {msg.role === 'user' ? (
                <>
                  {msg.files && msg.files.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: msg.content ? '6px' : 0 }}>
                      {msg.files.map((f, i) => (
                        <span key={i} style={{ fontSize: '0.75rem', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', padding: '2px 6px' }}>
                          {f.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
                </>
              ) : (
                <>
                  {msg.content && (
                    <Markdown
                      components={{
                        code({ className, children }) {
                          if (className === 'language-steps') {
                            return <CollapsibleSteps>{String(children)}</CollapsibleSteps>;
                          }
                          return <code className={className}>{children}</code>;
                        },
                        pre({ children }) {
                          const child = React.Children.toArray(children)[0];
                          if (React.isValidElement(child) && (child.props as { className?: string })?.className === 'language-steps') {
                            return <>{children}</>;
                          }
                          return <pre>{children}</pre>;
                        },
                      }}
                    >
                      {preprocessSteps(msg.content)}
                    </Markdown>
                  )}
                  {(msg.streaming || !msg.content) && <span style={{ opacity: 0.6 }}>...</span>}
                </>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '1rem', borderTop: '1px solid #ccc' }}>
        {files.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '0.5rem' }}>
            {files.map((f, i) => (
              <span key={i} style={{ fontSize: '0.75rem', backgroundColor: '#e0e0e0', borderRadius: '4px', padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {f.name}
                <span onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} style={{ cursor: 'pointer', fontWeight: 'bold' }}>&times;</span>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            style={{
              padding: '0.5rem',
              backgroundColor: 'transparent',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            📎
          </button>
          <textarea
            style={{
              flex: 1,
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              resize: 'none',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              opacity: disabled ? 0.6 : 1,
            }}
            rows={1}
            placeholder={disabled ? (messages[messages.length - 1]?.content ? "Typing..." : "Thinking...") : "Type a message..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
          <button
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: ((input.trim() || files.length > 0) && !disabled) ? '#1976d2' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: ((input.trim() || files.length > 0) && !disabled) ? 'pointer' : 'not-allowed',
            }}
            onClick={handleSend}
            disabled={(!input.trim() && files.length === 0) || disabled}
          >
            {disabled ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};
